/**
 * classifyScan is a pure UX hint that pre-routes a scanned/typed code in the
 * UI. Per AGENTS.md the **server is the only source of truth** for what a code
 * resolves to (legacy data may not match these heuristics), so callers must
 * still send the normalized value to the server lookup endpoint regardless of
 * the kind returned here.
 *
 * Rules:
 *   - "claim"   — EAN-13 starting with the claim_barcode private range "029".
 *   - "voucher" — any other 13-digit numeric string (assumed EAN-13 voucher).
 *   - "unknown" — anything else (text codes, partial input, etc.).
 *
 * Normalization: all whitespace stripped. (Hand-typed and barcode-scanner
 * input is otherwise left untouched.)
 */
export type ScanKind = "claim" | "voucher" | "unknown";

export interface ScanClassification {
  kind: ScanKind;
  normalized: string;
}

const CLAIM_PREFIX = window.__KARTIS_CONFIG__?.claimRejectPrefix ?? "029";
const EAN13_RE = /^\d{13}$/;

// Some barcode scanners silently drop a leading zero from a 13-digit EAN-13,
// producing a 12-digit string. Restore the zero when the truncated form would
// have started with "029" (the claim-barcode private range).
function restoreLeadingZero(s: string): string {
  if (s.length === 12 && s.startsWith("29")) return "0" + s;
  return s;
}

/**
 * Normalize a raw scan/type value: strip whitespace and restore a dropped
 * leading zero when the code matches the claim-barcode truncation pattern.
 * Call this on every scan value before sending it to the server or comparing
 * it against stored codes — regardless of whether classifyScan is needed.
 */
export function normalizeScan(raw: string): string {
  return restoreLeadingZero(raw.replace(/\s+/g, ""));
}

export function classifyScan(raw: string): ScanClassification {
  const normalized = normalizeScan(raw);
  if (normalized === "") return { kind: "unknown", normalized };
  if (EAN13_RE.test(normalized)) {
    if (normalized.startsWith(CLAIM_PREFIX)) {
      return { kind: "claim", normalized };
    }
    return { kind: "voucher", normalized };
  }
  return { kind: "unknown", normalized };
}
